import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StorageService } from 'src/app/services/auth/storage.service';
import { environment } from 'src/environments/environment';

interface ResponseB4A<T> {
  result: T;
}

export interface SetupConfiguration {
  configurationId?: number;
  configKey?: string;
  name?: string;
  description?: string;
  isEnabled?: boolean;
  value?: string | null;
  dateUpdated?: string;
}

export interface MercadoPagoRetentionThreeDsConfig {
  mercadoPagoRetentionThreeDsId?: number;
  isEnabled?: boolean;
  name?: string;
  description?: string;
  dateCreated?: string;
  dateUpdated?: string;
  updatedBy?: string | null;
}

export interface MercadoPagoAcquisitionThreeDsConfig {
  mercadoPagoAcquisitionThreeDsId?: number;
  isEnabled?: boolean;
  name?: string;
  description?: string;
  dateCreated?: string;
  dateUpdated?: string;
  updatedBy?: string | null;
}

export interface CheckoutJourneyCollectorConfig {
  checkoutJourneyCollectorConfigId?: number;
  isEnabled?: boolean;
  name?: string;
  description?: string;
  dateCreated?: string;
  dateUpdated?: string;
  updatedBy?: string | null;
}

export interface CheckoutGatewayPriority {
  checkoutGatewayPriorityId?: number;
  gatewayId?: number;
  gatewayOrder?: number;
  isActive?: boolean;
  dateCreated?: string;
  dateUpdated?: string;
  updatedBy?: string | null;
}

@Injectable({ providedIn: 'root' })
export class SetupConfigurationService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/setup/admin`;

  getCardVerificationConfigurations(): Observable<SetupConfiguration[]> {
    return this.http
      .get<ResponseB4A<SetupConfiguration[]>>(
        `${this.baseUrl}/configurations/card-verification`,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result ?? []));
  }

  getCardUpdaterConfigurations(): Observable<SetupConfiguration[]> {
    return this.http
      .get<ResponseB4A<SetupConfiguration[]>>(
        `${this.baseUrl}/configurations/card-updater`,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result ?? []));
  }

  updateConfiguration(
    configurationId: number,
    isEnabled: boolean
  ): Observable<SetupConfiguration> {
    return this.http
      .put<ResponseB4A<SetupConfiguration>>(
        `${this.baseUrl}/configurations/${configurationId}`,
        { isEnabled },
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  getMercadoPagoRetentionThreeDs(): Observable<MercadoPagoRetentionThreeDsConfig> {
    return this.http
      .get<ResponseB4A<MercadoPagoRetentionThreeDsConfig>>(
        `${this.baseUrl}/mercadopago-retention-3ds`,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  updateMercadoPagoRetentionThreeDs(
    isEnabled: boolean,
    updatedBy?: string
  ): Observable<MercadoPagoRetentionThreeDsConfig> {
    return this.http
      .put<ResponseB4A<MercadoPagoRetentionThreeDsConfig>>(
        `${this.baseUrl}/mercadopago-retention-3ds`,
        { isEnabled, updatedBy },
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  getMercadoPagoAcquisitionThreeDs(): Observable<MercadoPagoAcquisitionThreeDsConfig> {
    return this.http
      .get<ResponseB4A<MercadoPagoAcquisitionThreeDsConfig>>(
        `${this.baseUrl}/mercadopago-acquisition-3ds`,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  updateMercadoPagoAcquisitionThreeDs(
    isEnabled: boolean,
    updatedBy?: string
  ): Observable<MercadoPagoAcquisitionThreeDsConfig> {
    return this.http
      .put<ResponseB4A<MercadoPagoAcquisitionThreeDsConfig>>(
        `${this.baseUrl}/mercadopago-acquisition-3ds`,
        { isEnabled, updatedBy },
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  getCheckoutJourneyCollector(): Observable<CheckoutJourneyCollectorConfig> {
    return this.http
      .get<ResponseB4A<CheckoutJourneyCollectorConfig>>(
        `${this.baseUrl}/checkout-journey-collector`,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  updateCheckoutJourneyCollector(
    isEnabled: boolean,
    updatedBy?: string
  ): Observable<CheckoutJourneyCollectorConfig> {
    return this.http
      .put<ResponseB4A<CheckoutJourneyCollectorConfig>>(
        `${this.baseUrl}/checkout-journey-collector`,
        { isEnabled, updatedBy },
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  getCheckoutGatewayPriority(): Observable<CheckoutGatewayPriority[]> {
    return this.http
      .get<ResponseB4A<CheckoutGatewayPriority[]>>(
        `${this.baseUrl}/checkout-gateway-priority`,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result ?? []));
  }

  updateCheckoutGatewayPriority(
    gatewayIds: number[],
    updatedBy?: string
  ): Observable<CheckoutGatewayPriority[]> {
    return this.http
      .put<ResponseB4A<CheckoutGatewayPriority[]>>(
        `${this.baseUrl}/checkout-gateway-priority`,
        { gatewayIds, updatedBy },
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result ?? []));
  }

  private authHeaders(): HttpHeaders {
    const headers = new HttpHeaders();
    if (StorageService.userLogged && StorageService.token) {
      return headers.set('Authorization', `Bearer ${StorageService.token}`);
    }
    return headers;
  }
}
